import { Users, Mail, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    badge: "STELLEN",
    overline: "— Jobs bei Fritz Kamm",
    title_1: "Alle Stellen",
    title_2: "aktuell besetzt.",
    body:
      "Aktuell sind bei der Fritz Kamm alle bestehenden Stellen besetzt. Sollten Sie sich dennoch für eine spätere Zusammenarbeit interessieren, freuen wir uns auf Ihre Initiativbewerbung.",
    cta: "Initiativbewerbung senden",
    mail_label: "Bewerbung an",
  },
  en: {
    badge: "CAREERS",
    overline: "— Jobs at Fritz Kamm",
    title_1: "All positions",
    title_2: "currently filled.",
    body:
      "At the moment, all open positions at Fritz Kamm are filled. If you'd nevertheless like to work with us later on, we look forward to your unsolicited application.",
    cta: "Send an unsolicited application",
    mail_label: "Applications to",
  },
  fr: {
    badge: "EMPLOIS",
    overline: "— Emplois chez Fritz Kamm",
    title_1: "Tous les postes",
    title_2: "sont actuellement pourvus.",
    body:
      "Actuellement, tous les postes chez Fritz Kamm sont pourvus. Si vous êtes intéressé·e par une collaboration future, nous nous réjouissons de recevoir votre candidature spontanée.",
    cta: "Envoyer une candidature spontanée",
    mail_label: "Candidatures à",
  },
  it: {
    badge: "LAVORO",
    overline: "— Lavoro presso Fritz Kamm",
    title_1: "Tutte le posizioni",
    title_2: "sono attualmente occupate.",
    body:
      "Al momento tutte le posizioni presso Fritz Kamm sono occupate. Se desidera comunque una futura collaborazione, siamo lieti di ricevere la Sua candidatura spontanea.",
    cta: "Invia una candidatura spontanea",
    mail_label: "Candidature a",
  },
};

const APPLY_EMAIL = "sekretariat@f-kamm.ch";

export const Jobs = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const subject = encodeURIComponent(
    lang === "de" ? "Initiativbewerbung Fritz Kamm" :
    lang === "fr" ? "Candidature spontanée Fritz Kamm" :
    lang === "it" ? "Candidatura spontanea Fritz Kamm" :
    "Unsolicited application Fritz Kamm"
  );
  const mailHref = `mailto:${APPLY_EMAIL}?subject=${subject}`;

  return (
    <section
      id="jobs"
      data-testid="jobs-section"
      className="relative bg-[#0B0B0D] py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-40 left-1/3 w-[400px] h-[400px] rounded-full bg-white/[0.02] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-white/15 text-[10px]">
                <Users className="w-3 h-3" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className="reveal reveal-delay-2 text-zinc-300 text-base md:text-lg leading-relaxed mb-8">
              {c.body}
            </p>

            <div className="border border-white/10 p-5 bg-[#121214]">
              <div className="overline mb-3 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" />
                {c.mail_label}
              </div>
              <a
                href={mailHref}
                data-testid="jobs-apply"
                className="group inline-flex items-center justify-between gap-3 w-full text-white hover:text-zinc-300 transition-colors"
              >
                <span className="font-display text-lg md:text-xl tracking-tight">{APPLY_EMAIL}</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            </div>

            <a
              href={mailHref}
              data-testid="jobs-cta"
              className="mt-5 group inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-[12px] uppercase tracking-[0.16em] px-4 py-3 hover:bg-white hover:text-black transition-colors"
            >
              {c.cta}
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Jobs;
